// Service Worker Update Manager for PWA Auto-Update
class ServiceWorkerManager {
  constructor() {
    this.registration = null;
    this.updateAvailable = false;
    this.updatePromptShown = false;
    this.updateCallbacks = [];
    this.checkInterval = null;
    this.isUpdating = false;
    this.init();
  }

  init() {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    this.registerServiceWorker();
    this.setupMessageListener();
    this.startPeriodicUpdateCheck();
  }

  async registerServiceWorker() {
    try {
      // Get the current path to determine the correct scope
      const currentPath = window.location.pathname;
      const eventSlug = currentPath.match(/\/e\/([^\/]+)/)?.[1];

      // Use event-specific scope if we're on an event page, otherwise use root scope
      const scope = eventSlug ? `/e/${eventSlug}/` : "/";

      this.registration = await navigator.serviceWorker.register("/sw.js", {
        scope: scope,
      });

      // Listen for updates
      this.registration.addEventListener("updatefound", () => {
        this.handleUpdateFound();
      });

      // Check if there's already an update waiting
      if (this.registration.waiting) {
        this.updateAvailable = true;
        this.notifyUpdateAvailable();
      }
    } catch (error) {}
  }

  handleUpdateFound() {
    const newWorker = this.registration.installing;
    if (!newWorker) {
      return;
    }

    newWorker.addEventListener("statechange", () => {
      if (newWorker.state === "installed") {
        if (navigator.serviceWorker.controller) {
          this.updateAvailable = true;
          this.notifyUpdateAvailable();
        } else {
        }
      }
    });
  }

  setupMessageListener() {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data.type === "SW_ACTIVATED") {
        this.updateAvailable = false;
        this.updatePromptShown = false;
      }
    });
  }

  startPeriodicUpdateCheck() {
    // Check for updates every 30 minutes
    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, 30 * 60 * 1000);

    // Also check when the page becomes visible
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.checkForUpdates();
      }
    });
  }

  async checkForUpdates() {
    if (!this.registration) return;

    try {
      await this.registration.update();
    } catch (error) {}
  }

  notifyUpdateAvailable() {
    if (this.updatePromptShown) return;

    this.updateCallbacks.forEach((callback) => {
      if (typeof callback === "function") {
        callback();
      }
    });
  }

  onUpdateAvailable(callback) {
    this.updateCallbacks.push(callback);
  }

  async applyUpdate() {
    if (!this.registration) {
      throw new Error("No service worker registration found");
    }

    // Prevent multiple simultaneous update attempts
    if (this.isUpdating) {
      return;
    }
    this.isUpdating = true;

    if (!this.registration.waiting) {
      // If there's no waiting worker, try to update first
      await this.registration.update();

      // Wait a bit for the update to install
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Check again
      if (!this.registration.waiting) {
        // Still no waiting worker - just reload to be safe
        this.isUpdating = false;
        window.location.reload();
        return;
      }
    }

    try {
      // Tell the waiting service worker to skip waiting
      this.registration.waiting.postMessage({ type: "SKIP_WAITING" });

      // Set up reload mechanism with proper cleanup
      let reloadTriggered = false;

      const triggerReload = () => {
        if (reloadTriggered) return;
        reloadTriggered = true;
        this.isUpdating = false;
        window.location.reload();
      };

      // Listen for controllerchange to reload
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        triggerReload,
        { once: true }
      );

      // Fallback reload after 3 seconds if controllerchange doesn't fire
      setTimeout(triggerReload, 3000);
    } catch (error) {
      // If anything fails, just reload
      this.isUpdating = false;
      window.location.reload();
    }
  }

  async skipUpdate() {
    this.updateAvailable = false;
    this.updatePromptShown = false;
  }

  getUpdateStatus() {
    const hasWaitingWorker = this.registration && this.registration.waiting;
    const hasInstallingWorker =
      this.registration && this.registration.installing;

    return {
      updateAvailable: this.updateAvailable && hasWaitingWorker,
      registration: this.registration,
      hasWaitingWorker,
      hasInstallingWorker,
    };
  }

  destroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }
}

// Create global instance
const serviceWorkerManager = new ServiceWorkerManager();

export default serviceWorkerManager;
